import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useAuthStore } from "@/store/authStore"
import { trpc } from "@/lib/trpc"
import { REGIONS } from "@/constants/exam"

const schema = z
  .object({
    username: z.string().min(1, "请输入用户名"),
    email: z.string().email("请输入有效邮箱"),
    password: z.string().min(6, "密码至少 6 位"),
    confirmPassword: z.string().min(6, "请再次输入密码"),
    role: z.string().min(1, "请选择角色"),
    stage: z.string().min(1, "请选择学段"),
    grade: z.string().min(1, "请选择年级"),
    province: z.string().optional(),
    city: z.string().optional(),
    district: z.string().optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ["confirmPassword"],
    message: "两次输入的密码不一致",
  })

type FormValues = z.infer<typeof schema>

export default function RegisterPage() {
  const navigate = useNavigate()
  const { register: registerUser, loading, error } = useAuthStore()

  // Fetch lookup data using tRPC hooks
  const { data: stages = [] } = trpc.lookup.stages.useQuery()
  const { data: roles = [] } = trpc.lookup.roles.useQuery()

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
  })

  const stage = watch("stage")
  const province = watch("province")
  const city = watch("city")

  // Fetch grades when stage changes
  const { data: grades = [] } = trpc.lookup.grades.useQuery(
    { stageCode: stage },
    { enabled: !!stage }
  )

  // Get cities from REGIONS constant
  const selectedProvince = REGIONS.find(p => p.name === province)
  const cities = selectedProvince?.children || []

  // Reset grade when stage changes
  useEffect(() => {
    if (stage) {
      setValue("grade", "")
    }
  }, [stage, setValue])

  // Reset city/district when province changes
  useEffect(() => {
    if (province) {
      setValue("city", "")
      setValue("district", "")
    }
  }, [province, setValue])

  // Reset district when city changes
  useEffect(() => {
    if (city) {
      setValue("district", "")
    }
  }, [city, setValue])

  const onSubmit = async (values: FormValues) => {
    try {
      await registerUser({
        username: values.username,
        email: values.email,
        password: values.password,
        role: values.role,
        stage: values.stage,
        grade: values.grade,
        province: values.province || undefined,
        city: values.city || undefined,
        district: values.district || undefined,
      })
      navigate("/", { replace: true })
    } catch {
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="w-full max-w-sm space-y-4 rounded-md border bg-white p-6 shadow-sm"
      >
        <h1 className="text-center text-xl font-semibold">注册</h1>
        <div className="space-y-1">
          <label className="text-sm">用户名</label>
          <Input {...register("username")} />
          {errors.username && (
            <p className="text-xs text-red-500">{errors.username.message}</p>
          )}
        </div>
        <div className="space-y-1">
          <label className="text-sm">邮箱</label>
          <Input {...register("email")} />
          {errors.email && (
            <p className="text-xs text-red-500">{errors.email.message}</p>
          )}
        </div>
        <div className="space-y-1">
          <label className="text-sm">密码</label>
          <Input type="password" {...register("password")} />
          {errors.password && (
            <p className="text-xs text-red-500">{errors.password.message}</p>
          )}
        </div>
        <div className="space-y-1">
          <label className="text-sm">确认密码</label>
          <Input type="password" {...register("confirmPassword")} />
          {errors.confirmPassword && (
            <p className="text-xs text-red-500">
              {errors.confirmPassword.message}
            </p>
          )}
        </div>
        <div className="space-y-1">
          <label className="text-sm">角色</label>
          <select
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/40"
            {...register("role")}
          >
            <option value="">请选择角色</option>
            {roles.map((r) => (
              <option key={r.code} value={r.code}>
                {r.name}
              </option>
            ))}
          </select>
          {errors.role && (
            <p className="text-xs text-red-500">{errors.role.message}</p>
          )}
        </div>
        <div className="space-y-1">
          <label className="text-sm">学段</label>
          <select
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/40"
            {...register("stage")}
          >
            <option value="">请选择学段</option>
            {stages.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name}
              </option>
            ))}
          </select>
          {errors.stage && (
            <p className="text-xs text-red-500">{errors.stage.message}</p>
          )}
        </div>
        <div className="space-y-1">
          <label className="text-sm">年级</label>
          <select
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/40"
            {...register("grade")}
            disabled={!stage}
          >
            <option value="">请选择年级</option>
            {grades.map((g) => (
              <option key={g.code} value={g.code}>
                {g.name}
              </option>
            ))}
          </select>
          {errors.grade && (
            <p className="text-xs text-red-500">{errors.grade.message}</p>
          )}
        </div>
        <div className="space-y-1">
          <label className="text-sm">省份</label>
          <select
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/40"
            {...register("province")}
          >
            <option value="">请选择省份</option>
            {REGIONS.map((p) => (
              <option key={p.name} value={p.name}>
                {p.name}
              </option>
            ))}
          </select>
          {errors.province && (
            <p className="text-xs text-red-500">{errors.province.message}</p>
          )}
        </div>
        {cities.length > 0 && (
          <div className="space-y-1">
            <label className="text-sm">城市</label>
            <select
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/40"
              {...register("city")}
            >
              <option value="">请选择城市</option>
              {cities.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        )}
        {error && <p className="text-xs text-red-500">{error}</p>}
        <Button type="submit" className="mt-2 w-full" disabled={loading}>
          注册
        </Button>
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={() => navigate("/login")}
        >
          返回登录
        </Button>
      </form>
    </div>
  )
}
